<template>
  <ElDialog
    v-model="visible"
    :title="title"
    width="640px"
    class="address-modal"
  >
    <div class="form">
      <div>
        <label class="label">收货人姓名</label>
        <ElInput v-model="local.name" placeholder="请输入" class="input-lg">
          <template #prefix
            ><i class="iconfont icon-gerenzhongxin"></i
          ></template>
        </ElInput>
      </div>
      <div>
        <label class="label">联系方式</label>
        <ElInput v-model="local.phone" placeholder="请输入" class="input-lg">
          <template #prefix><i class="iconfont icon-shoujihaoma"></i></template>
        </ElInput>
      </div>
      <div>
        <label class="label">所在地区</label>
        <div class="cascader-wrapper">
          <i class="iconfont icon-dizhi cascader-icon"></i>
          <ElCascader
            ref="cascaderRef"
            v-model="cascaderValue"
            :props="cascaderProps"
            placeholder="请选择"
            style="width: 100%"
            class="custom-cascader input-lg"
            @change="onCascaderChange"
          />
        </div>
      </div>
      <div>
        <label class="label">详细地址</label>
        <ElInput v-model="local.detail" placeholder="请输入" class="input-lg">
          <template #prefix><i class="iconfont icon-bianjidizhi"></i></template>
        </ElInput>
      </div>
      <div>
        <label class="label">是否设为默认地址</label>
        <div class="radios">
          <ElRadio v-model="local.isDefault" :label="true">是</ElRadio>
          <ElRadio v-model="local.isDefault" :label="false">否</ElRadio>
        </div>
      </div>
    </div>
    <template #footer>
      <ElButton type="primary" class="save" :disabled="!canSave" @click="onSave"
        >保存收货地址</ElButton
      >
    </template>
  </ElDialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { User, Phone, EditPen } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";

const api = useApi();

type Address = {
  id?: number;
  name: string;
  phone: string;
  region: string;
  detail: string;
  isDefault: boolean;
};
const props = defineProps<{ modelValue: boolean; initial?: Address }>();
const emit = defineEmits<{
  (e: "update:modelValue", v: boolean): void;
  (e: "success"): void;
}>();

const visible = ref(props.modelValue);
watch(
  () => props.modelValue,
  (v) => (visible.value = v)
);
watch(visible, (v) => emit("update:modelValue", v));

// Internal form state matching API requirements
const local = ref<Address>({
  name: "",
  phone: "",
  region: "",
  detail: "",
  isDefault: false,
});
// Extra fields for API
const apiData = ref({
  provinceId: 0,
  province: "",
  cityId: 0,
  city: "",
  areaId: 0,
  area: "",
});
const cascaderValue = ref<number[]>([]);

const cascaderProps = {
  lazy: true,
  lazyLoad: async (node: any, resolve: any) => {
    const { level } = node;
    // Level 0: load provinces (pid=0)
    // Level 1: load cities (pid=node.value)
    // Level 2: load areas (pid=node.value)
    const pid = level === 0 ? 0 : node.value;

    try {
      const res = await api.get<any>("/p/area/listByPid", { pid });
      if (res.ok) {
        const list = (res.data || res.raw || []).map((item: any) => ({
          value: item.areaId,
          label: item.areaName,
          leaf: level >= 2, // 0->1->2(leaf)
        }));
        resolve(list);
      } else {
        resolve([]);
      }
    } catch {
      resolve([]);
    }
  },
};

const onCascaderChange = () => {
  // Optional: handle change immediately if needed
};

const cascaderRef = ref();

// Initial data loading
watch(
  () => props.initial,
  async (v) => {
    if (v && v.id) {
      // Edit mode: fetch details
      const res = await api.get<any>(`/p/address/addrInfo/${v.id}`);
      if (res.ok && res.data) {
        const d = res.data;
        local.value = {
          id: d.addrId,
          name: d.receiver || d.realName || "",
          phone: d.mobile || d.phone || "",
          region: "", // Will be constructed or ignored
          detail: d.addr || d.addrDetail || "",
          isDefault: d.commonAddr === 1 || d.isDefault === true, // Check field name
        };
        apiData.value = {
          provinceId: d.provinceId,
          province: d.province,
          cityId: d.cityId,
          city: d.city,
          areaId: d.areaId,
          area: d.area,
        };
        cascaderValue.value = [d.provinceId, d.cityId, d.areaId];
      }
    } else {
      // Add mode
      local.value = {
        name: "",
        phone: "",
        region: "",
        detail: "",
        isDefault: false,
      };
      apiData.value = {
        provinceId: 0,
        province: "",
        cityId: 0,
        city: "",
        areaId: 0,
        area: "",
      };
      cascaderValue.value = [];
    }
  },
  { immediate: true }
);

const title = computed(() => (props.initial ? "编辑收货地址" : "新增收货地址"));
const canSave = computed(
  () =>
    !!local.value.name &&
    !!local.value.phone &&
    cascaderValue.value.length === 3 &&
    !!local.value.detail
);

const onSave = async () => {
  // We need to get the labels for the selected IDs.
  // Since we are using lazy loading, we might rely on the `apiData` populated during edit,
  // BUT if user changed the selection, `apiData` is stale.
  // We MUST get the labels from the cascader.

  let labels = ["", "", ""];
  if (cascaderRef.value) {
    const nodes = cascaderRef.value.getCheckedNodes();
    if (nodes && nodes.length > 0) {
      labels = nodes[0].pathLabels;
    }
  }

  // If we can't get labels (e.g. during initial load without change), we might fallback to `apiData` values if IDs match.
  // But for new selection, we need `cascaderRef`.

  const payload = {
    receiver: local.value.name,
    mobile: local.value.phone,
    addr: local.value.detail,
    province: labels[0] || apiData.value.province,
    provinceId: cascaderValue.value[0],
    city: labels[1] || apiData.value.city,
    cityId: cascaderValue.value[1],
    area: labels[2] || apiData.value.area,
    areaId: cascaderValue.value[2],
    userType: 0,
    addrId: local.value.id,
    commonAddr: local.value.isDefault ? 1 : 0,
  };

  const isEdit = !!local.value.id;
  const url = isEdit ? "/p/address/updateAddr" : "/p/address/addAddr";
  const method = isEdit ? api.put : api.post;

  const res = await method(url, payload);
  if (res.ok) {
    ElMessage.success(isEdit ? "修改成功" : "新增成功");
    emit("success");
    visible.value = false;
  } else {
    ElMessage.error(res.msg || "保存失败");
  }
};
</script>

<style scoped>
.form {
  display: grid;
  gap: 0.4rem;
}
.label {
  color: #333;
  display: block;
  font-weight: 500;
  margin-bottom: 0.1rem;
}
.radios {
  display: flex;
  gap: 0.2rem;
  align-items: center;
}
.save {
  width: 100%;
  height: 0.64rem;
}

.cascader-wrapper {
  position: relative;
  width: 100%;
}
.cascader-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  color: #a8abb2;
  font-size: 14px;
  pointer-events: none;
}
:deep(.custom-cascader .el-input__wrapper) {
  padding-left: 30px;
  min-height: 0.64rem;
  height: 0.64rem;
}
/* Fallback for older Element Plus versions where padding is on inner */
:deep(.custom-cascader .el-input__inner) {
  padding-left: 2px;
}

:deep(.form .el-input__wrapper) {
  min-height: 0.64rem;
  height: 0.64rem;
}
:deep(.form .el-input__inner) {
  height: 0.64rem;
  line-height: 0.64rem;
}

.input-lg :deep(.el-input__wrapper) {
  min-height: 0.64rem;
  height: 0.64rem;
}
.input-lg :deep(.el-input__inner) {
  height: 0.64rem;
  line-height: 0.64rem;
}
.input-lg :deep(.el-input) {
  --el-input-height: 0.64rem;
  --el-input-small-height: 0.64rem;
  --el-input-line-height: 0.64rem;
}

/* Use Element Plus CSS variables to ensure consistent input height across wrappers */
:deep(.form .el-input) {
  --el-input-height: 0.64rem;
  --el-input-line-height: 0.64rem;
}
:deep(.custom-cascader .el-input) {
  --el-input-height: 0.64rem;
  --el-input-line-height: 0.64rem;
}

</style>
